/**
 * Create-ASN wizard (CR-4888 Slice C).
 *
 * 3-step flow: Source & Lines → Shipment Details → Review & Submit.
 * ASN number is auto-generated server-side; distance_km is auto-calculated
 * from supplier ↔ customer geocoded sites — both are read-only on the wire.
 */
import { useState, useEffect } from 'react';
import { X, Package } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { purchasingAPI, callOffAPI } from '../../lib/api';
import { ASNModalProps, ASNScheduleDetail } from './asnModalTypes';
import { StepIndicator } from './asn-wizard/StepIndicator';
import { SourceLinesStep } from './asn-wizard/SourceLinesStep';
import { ShipmentDetailsStep } from './asn-wizard/ShipmentDetailsStep';
import { ReviewStep } from './asn-wizard/ReviewStep';
import { ImportASNLinesModal } from './asn-wizard/ImportASNLinesModal';
import {
  ASNFormData,
  DeliveryLineOption,
  INITIAL_FORM_DATA,
  SourceType,
  StepIndex,
} from './asn-wizard/types';

/**
 * Build supplier-style tracking default: {3-letter prefix}{trailing digits}.
 * Mirrors backend core.utils.call_off_tracking_default — keep both in sync.
 */
function buildSupplierTrackingDefault(supplierName: string | undefined, reference: string | undefined): string {
  if (!supplierName || !reference) return '';
  const tokens = supplierName.split(/\s+/).map((t) => t.replace(/[^A-Za-z]/g, '')).filter((t) => t.length >= 3);
  const prefix = tokens.length
    ? tokens.reduce((a, b) => (b.length > a.length ? b : a)).slice(0, 3).toUpperCase()
    : '';
  const digitsMatch = reference.match(/(\d+)\s*$/);
  const digits = digitsMatch ? digitsMatch[1] : '';
  return prefix && digits ? `${prefix}${digits}` : '';
}

export function CreateASNModal({ isOpen, onClose, onSubmit, initialCallOffId }: ASNModalProps) {
  const [currentStep, setCurrentStep] = useState<StepIndex>(1);
  const [sourceType, setSourceType] = useState<SourceType>(
    initialCallOffId ? 'delivery_lines' : 'schedules'
  );
  const [formData, setFormData] = useState<ASNFormData>(INITIAL_FORM_DATA);
  const [submitting, setSubmitting] = useState(false);
  const [schedules, setSchedules] = useState<ASNScheduleDetail[]>([]);
  const [deliveryLines, setDeliveryLines] = useState<DeliveryLineOption[]>([]);
  const [loadingSchedules, setLoadingSchedules] = useState(true);
  const [loadingDeliveryLines, setLoadingDeliveryLines] = useState(true);
  const [isImportOpen, setIsImportOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (initialCallOffId) {
      setSourceType('delivery_lines');
      // Default the date field to the source call-off's pickup date and
      // build a tracking-number suggestion so the supplier doesn't retype them.
      callOffAPI.getCallOff(initialCallOffId)
        .then((res) => {
          const co = res.data;
          setFormData((prev) => {
            const next = { ...prev };
            if (!prev.expected_delivery_date && co.pickup_date) {
              next.expected_delivery_date = co.pickup_date;
            }
            if (!prev.tracking_number) {
              const suggested = buildSupplierTrackingDefault(co.supplier_name, co.reference);
              if (suggested) next.tracking_number = suggested;
            }
            return next;
          });
        })
        .catch(() => { /* non-critical — supplier can fill manually */ });
    }
    fetchAvailableSchedules();
    fetchAvailableDeliveryLines();
  }, [isOpen, initialCallOffId]);

  const fetchAvailableSchedules = async () => {
    try {
      setLoadingSchedules(true);
      const response = await purchasingAPI.getSchedules({
        status: 'scheduled',
        ordering: 'schedule_date',
      });
      setSchedules(response.data.results);
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    } finally {
      setLoadingSchedules(false);
    }
  };

  const fetchAvailableDeliveryLines = async () => {
    try {
      setLoadingDeliveryLines(true);
      const response = await purchasingAPI.getAvailableDeliveryLines();
      const lines = response.data as unknown as DeliveryLineOption[];
      setDeliveryLines(lines);
      if (initialCallOffId) {
        const matchIds = lines
          .filter((l) => l.call_off === initialCallOffId)
          .map((l) => l.id);
        if (matchIds.length > 0) {
          setFormData((prev) => ({
            ...prev,
            delivery_line_ids: Array.from(new Set([...prev.delivery_line_ids, ...matchIds])),
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch delivery lines:', error);
    } finally {
      setLoadingDeliveryLines(false);
    }
  };

  const handleFieldChange = (field: keyof ASNFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSourceChange = (newSource: SourceType) => {
    setSourceType(newSource);
    setFormData((prev) => ({ ...prev, schedules: [], delivery_line_ids: [] }));
  };

  const handleScheduleToggle = (id: number) => {
    setFormData((prev) => {
      const next = prev.schedules.includes(id)
        ? prev.schedules.filter((s) => s !== id)
        : [...prev.schedules, id];
      return { ...prev, schedules: next };
    });
  };

  const handleDeliveryLineToggle = (id: number) => {
    setFormData((prev) => {
      const next = prev.delivery_line_ids.includes(id)
        ? prev.delivery_line_ids.filter((l) => l !== id)
        : [...prev.delivery_line_ids, id];
      return { ...prev, delivery_line_ids: next };
    });
  };

  // When selected delivery lines all belong to a single call-off, auto-fill
  // pickup-date + tracking-number from that call-off (only fills blanks so a
  // supplier's manual edits stick). Triggers regardless of how the modal was
  // opened — direct from the ASN page works the same as Create-ASN-from-CO.
  useEffect(() => {
    if (formData.delivery_line_ids.length === 0) return;
    const selectedLines = deliveryLines.filter((l) => formData.delivery_line_ids.includes(l.id));
    const callOffIds = Array.from(new Set(selectedLines.map((l) => l.call_off).filter((id): id is number => id != null)));
    if (callOffIds.length !== 1) return;
    const callOffId = callOffIds[0];
    if (callOffId === initialCallOffId) return; // already handled on open
    callOffAPI.getCallOff(callOffId)
      .then((res) => {
        const co = res.data;
        setFormData((prev) => {
          const next = { ...prev };
          if (!prev.expected_delivery_date && co.pickup_date) {
            next.expected_delivery_date = co.pickup_date;
          }
          if (!prev.tracking_number) {
            const suggested = buildSupplierTrackingDefault(co.supplier_name, co.reference);
            if (suggested) next.tracking_number = suggested;
          }
          return next;
        });
      })
      .catch(() => { /* non-critical */ });
  }, [formData.delivery_line_ids, deliveryLines, initialCallOffId]);

  const handleSelectAllInGroup = (lines: DeliveryLineOption[]) => {
    const lineIds = lines.map((l) => l.id);
    setFormData((prev) => {
      const allSelected = lineIds.every((id) => prev.delivery_line_ids.includes(id));
      const next = allSelected
        ? prev.delivery_line_ids.filter((id) => !lineIds.includes(id))
        : Array.from(new Set([...prev.delivery_line_ids, ...lineIds]));
      return { ...prev, delivery_line_ids: next };
    });
  };

  const handleExcelImported = async (importedIds: number[]) => {
    setSourceType('delivery_lines');
    setFormData((prev) => ({
      ...prev,
      schedules: [],
      delivery_line_ids: Array.from(new Set([...prev.delivery_line_ids, ...importedIds])),
    }));
    await fetchAvailableDeliveryLines();
    setIsImportOpen(false);
  };

  const hasSelection =
    sourceType === 'schedules'
      ? formData.schedules.length > 0
      : formData.delivery_line_ids.length > 0;

  const shipmentDetailsValid =
    !!formData.tracking_number &&
    !!formData.expected_delivery_date &&
    !!formData.weight_kg &&
    (formData.courier !== 'other' || !!formData.courier_other);

  const canAdvance =
    currentStep === 1 ? hasSelection : currentStep === 2 ? shipmentDetailsValid : true;

  const resetAndClose = () => {
    setFormData(INITIAL_FORM_DATA);
    setSourceType('schedules');
    setCurrentStep(1);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload: Record<string, string | number | boolean | number[]> = {
        courier: formData.courier,
        courier_other: formData.courier_other,
        tracking_number: formData.tracking_number,
        invoice_number: formData.invoice_number,
        method_of_transport: formData.method_of_transport,
        weight_kg: formData.weight_kg,
        expected_delivery_date: formData.expected_delivery_date,
        notes: formData.notes,
      };
      if (sourceType === 'schedules') {
        payload['schedules'] = formData.schedules;
      } else {
        payload['delivery_line_ids'] = formData.delivery_line_ids;
      }
      await onSubmit(payload as Record<string, string | number | boolean>);
      resetAndClose();
    } catch (error) {
      console.error('Failed to create ASN:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold">Create Advanced Shipping Notice</h3>
          </div>
          <button
            onClick={resetAndClose}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <CardContent className="p-4 space-y-4">
          <StepIndicator currentStep={currentStep} />

          {currentStep === 1 && (
            <SourceLinesStep
              sourceType={sourceType}
              onSourceChange={handleSourceChange}
              formData={formData}
              onToggleSchedule={handleScheduleToggle}
              onToggleDeliveryLine={handleDeliveryLineToggle}
              onSelectAllInGroup={handleSelectAllInGroup}
              onOpenExcelImport={() => setIsImportOpen(true)}
              schedules={schedules}
              deliveryLines={deliveryLines}
              loadingSchedules={loadingSchedules}
              loadingDeliveryLines={loadingDeliveryLines}
            />
          )}
          {currentStep === 2 && (
            <ShipmentDetailsStep
              formData={formData}
              onChange={handleFieldChange}
              isCallOffSourced={sourceType === 'delivery_lines'}
            />
          )}
          {currentStep === 3 && <ReviewStep sourceType={sourceType} formData={formData} />}

          <div className="flex justify-between gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={resetAndClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <div className="flex gap-3">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep((s) => (s - 1) as StepIndex)}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                >
                  Back
                </button>
              )}
              {currentStep < 3 ? (
                <button
                  type="button"
                  onClick={() => setCurrentStep((s) => (s + 1) as StepIndex)}
                  disabled={!canAdvance}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !hasSelection || !shipmentDetailsValid}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Package className="h-4 w-4" />
                  {submitting ? 'Creating...' : 'Create ASN'}
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <ImportASNLinesModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImported={handleExcelImported}
      />
    </div>
  );
}
